import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { CheckCircle2, MousePointerClick, Eye, XCircle, Send, Clock } from "lucide-react";
import { fetchJson, formatDateTime, type Timeline } from "@/lib/comms";
import { ErrorState, StatusBadge, TableSkeleton } from "./shared";

export interface TimelineTarget { contactId?: string; userId?: string; email?: string }

export default function ContactTimelineSheet({ target, onClose }: { target: TimelineTarget | null; onClose: () => void }) {
  const qs = target ? new URLSearchParams(Object.entries(target).filter(([, v]) => !!v) as [string, string][]).toString() : "";
  const { data, isLoading, error } = useQuery<Timeline>({ queryKey: ["/api/admin/comms/timeline", qs], queryFn: () => fetchJson(`/api/admin/comms/timeline?${qs}`), enabled: !!target });
  return (
    <Sheet open={!!target} onOpenChange={o => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl overflow-y-auto" data-testid="sheet-contact-timeline">
        <SheetHeader><SheetTitle>Comms history</SheetTitle><SheetDescription>Every message, open, click, list and journey for this person.</SheetDescription></SheetHeader>
        <div className="mt-4 space-y-5">
          {error ? <ErrorState error={error} /> : isLoading || !data ? <TableSkeleton rows={5} cols={3} /> : data.contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No contact record yet. Contacts are built from the user table nightly; press Refresh on the Overview tab to build them now.</p>
          ) : (
            <>
              <div className="space-y-2">
                {data.contacts.map((c: any) => (
                  <div key={c.id} className="rounded-md border p-3 text-sm">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium">{[c.first_name, c.last_name].filter(Boolean).join(" ") || "No name"}</span>
                      <Badge variant="outline">{c.kind}</Badge>
                      {c.email && <span className="text-muted-foreground break-all">{c.email}</span>}
                      {c.email && <StatusBadge status={c.email_status} />}
                      {c.phone && <span className="text-muted-foreground">{c.phone}</span>}
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(c.tags || []).map((t: string) => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                      {(c.lists || []).map((l: any) => <Badge key={l.id} variant="outline" className="text-[10px]">list: {l.name}</Badge>)}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">Source {c.source} · last engaged {formatDateTime(c.last_engaged_at)}</div>
                  </div>
                ))}
              </div>

              {data.enrollments.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Journeys</h4>
                  <ul className="text-sm space-y-1">
                    {data.enrollments.map(e => (
                      <li key={e.id} className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{e.journey_name}</span>
                        <Badge variant={e.status === "active" ? "default" : "outline"}>{e.status}</Badge>
                        <span className="text-xs text-muted-foreground">{e.status === "active" ? `next step ${e.next_position} ${formatDateTime(e.next_run_at)}` : `${e.exit_reason || "finished"} ${formatDateTime(e.finished_at)}`}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Messages</h4>
                {data.sends.length === 0 ? <p className="text-sm text-muted-foreground">Nothing sent yet.</p> : (
                  <ul className="space-y-2">
                    {data.sends.map(s => (
                      <li key={s.id} className="rounded-md border p-2.5 text-sm">
                        <div className="flex flex-wrap items-center gap-2">
                          <Send className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="font-medium">{s.subject || s.channel}</span>
                          <StatusBadge status={s.status} />
                          <span className="text-xs text-muted-foreground ml-auto">{formatDateTime(s.sent_at || s.scheduled_for)}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {s.campaign_name ? `Campaign: ${s.campaign_name}` : s.journey_name ? `Journey: ${s.journey_name}${s.step_name ? ` · ${s.step_name}` : ""}` : s.automation_name ? `Automation: ${s.automation_name}` : "Ad hoc"} · {s.channel} · {s.address}
                        </div>
                        <div className="flex flex-wrap gap-3 text-xs mt-1">
                          {s.delivered_at && <span className="inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3 h-3" />delivered {formatDateTime(s.delivered_at)}</span>}
                          {s.opened_at && <span className="inline-flex items-center gap-1 text-sky-700"><Eye className="w-3 h-3" />opened {formatDateTime(s.opened_at)}</span>}
                          {s.clicked_at && <span className="inline-flex items-center gap-1 text-amber-700"><MousePointerClick className="w-3 h-3" />clicked {formatDateTime(s.clicked_at)}</span>}
                          {s.status === "queued" && <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />scheduled {formatDateTime(s.scheduled_for)}</span>}
                          {s.error && <span className="inline-flex items-center gap-1 text-destructive"><XCircle className="w-3 h-3" />{s.error}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {data.events.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Events</h4>
                  <ul className="text-xs text-muted-foreground space-y-0.5 max-h-56 overflow-y-auto">
                    {data.events.map(e => <li key={e.id}>{formatDateTime(e.occurred_at)} · {e.type}{e.url ? ` · ${e.url}` : ""}{e.provider ? ` · ${e.provider}` : ""}</li>)}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
